import React from "react";
import { Link } from "react-router-dom";
import {
  Package,
  ScanLine,
  BarChart3,
  Users,
  Check,
  ChevronDown,
  ArrowRight,
  Sparkles,
  Zap,
  Crown,
  Quote,
  ShoppingBag,
  Wifi,
  Barcode as BarcodeIcon,
} from "lucide-react";

/**
 * WARM & ARTISAN redesign preview.
 * Live at /preview/landing — NOT linked from anywhere so the current /
 * stays untouched. Purely for visual review before committing to a full
 * design rollout across the app.
 *
 * Palette:
 *   Cream       #F7F1E6  page background
 *   Ivory       #FDFAF3  card background
 *   Warm Ink    #1F1815  primary text
 *   Muted       #6B5F52  secondary text
 *   Terracotta  #C5583A  primary accent (buttons, badges, highlight tier)
 *   Sage        #6B8E5A  positive / success
 *   Warm Border #E8DDC9  hairlines
 *
 * Fonts:
 *   Fraunces (serif, variable) for hero + section headings
 *   Inter for body + UI
 */

export default function LandingPreview() {
  // Load Google Fonts once on mount
  React.useEffect(() => {
    const id = "vv-preview-fonts";
    if (document.getElementById(id)) return;
    const link1 = document.createElement("link");
    link1.rel = "preconnect";
    link1.href = "https://fonts.googleapis.com";
    const link2 = document.createElement("link");
    link2.rel = "preconnect";
    link2.href = "https://fonts.gstatic.com";
    link2.crossOrigin = "anonymous";
    const link3 = document.createElement("link");
    link3.id = id;
    link3.rel = "stylesheet";
    link3.href =
      "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght,SOFT,WONK@9..144,300..900,0..100,0..1&family=Inter:wght@400;500;600;700&display=swap";
    document.head.append(link1, link2, link3);
  }, []);

  // Read accent from URL (?accent=plum) so we can screenshot each variant.
  const PALETTES = {
    terracotta: { name: "Terracotta", primary: "#C5583A", primaryHover: "#B04A2E", tint: "#F0E6D2", tintText: "#6B5F52", success: "#6B8E5A", successTint: "#E6EDD8", successText: "#4A6B3E" },
    burnt:      { name: "Burnt Orange", primary: "#C2410C", primaryHover: "#9A3308", tint: "#F3D9C6", tintText: "#6B5346", success: "#6B8E5A", successTint: "#E6EDD8", successText: "#4A6B3E" },
    rust:       { name: "Rust",         primary: "#A85B37", primaryHover: "#8E4A2A", tint: "#EDD9C9", tintText: "#6B5346", success: "#6B8E5A", successTint: "#E6EDD8", successText: "#4A6B3E" },
    amber:      { name: "Amber",        primary: "#D97706", primaryHover: "#B45309", tint: "#F5E4C0", tintText: "#6B5946", success: "#6B8E5A", successTint: "#E6EDD8", successText: "#4A6B3E" },
    persimmon:  { name: "Persimmon",    primary: "#EA580C", primaryHover: "#C24A0A", tint: "#F7DBC7", tintText: "#6B4E3F", success: "#6B8E5A", successTint: "#E6EDD8", successText: "#4A6B3E" },
    peach:      { name: "Soft Peach",   primary: "#F97316", primaryHover: "#D65C0F", tint: "#FADFC9", tintText: "#6B5346", success: "#6B8E5A", successTint: "#E6EDD8", successText: "#4A6B3E" },
    plum:       { name: "Deep Plum",  primary: "#7A3E52", primaryHover: "#66334A", tint: "#EDDDE3", tintText: "#6B5F5F", success: "#6B8E5A", successTint: "#E6EDD8", successText: "#4A6B3E" },
    forest:     { name: "Forest",     primary: "#4F6B3E", primaryHover: "#405831", tint: "#DDE7D3", tintText: "#5A6152", success: "#8B7B4A", successTint: "#EDE7D3", successText: "#6B5F3E" },
    navy:       { name: "Ink Navy",   primary: "#2E4A6B", primaryHover: "#253A55", tint: "#D9E1EC", tintText: "#5A6472", success: "#6B8E5A", successTint: "#E6EDD8", successText: "#4A6B3E" },
    gold:       { name: "Warm Gold",  primary: "#B0863C", primaryHover: "#966F30", tint: "#EDE3CB", tintText: "#6B5F4A", success: "#6B8E5A", successTint: "#E6EDD8", successText: "#4A6B3E" },
    burgundy:   { name: "Burgundy",   primary: "#8B3A47", primaryHover: "#742F3B", tint: "#EDD9DE", tintText: "#6B5F5F", success: "#6B8E5A", successTint: "#E6EDD8", successText: "#4A6B3E" },
    cyan:       { name: "Cyan",       primary: "#0891B2", primaryHover: "#0E7490", tint: "#D6ECF2", tintText: "#3F6672", success: "#6B8E5A", successTint: "#E6EDD8", successText: "#4A6B3E" },
    teal:       { name: "Deep Teal",  primary: "#0F766E", primaryHover: "#115E59", tint: "#D5E8E5", tintText: "#3F5C58", success: "#B0863C", successTint: "#EDE3CB", successText: "#6B5F4A" },
  };
  const paletteKeys = Object.keys(PALETTES);
  const urlKey = (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("accent") : "") || "terracotta";
  const [paletteKey, setPaletteKey] = React.useState(paletteKeys.includes(urlKey) ? urlKey : "terracotta");
  const P = PALETTES[paletteKey];

  const [yearly, setYearly] = React.useState(false);
  const [openFaq, setOpenFaq] = React.useState(0);

  return (
    <div
      style={{
        background: "#F7F1E6",
        color: "#1F1815",
        fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
      }}
      className="min-h-screen antialiased"
    >
      <style>{`
        .heading { font-family: 'Fraunces', ui-serif, Georgia, serif; font-variation-settings: "opsz" 144, "SOFT" 40; letter-spacing: -0.02em; }
        .heading-italic { font-style: italic; font-variation-settings: "opsz" 144, "SOFT" 100, "WONK" 1; }
        .btn-primary { background:${P.primary}; color:#FDFAF3; border-radius:999px; padding:14px 26px; font-weight:600; font-size:14px; display:inline-flex; align-items:center; gap:8px; transition:all 200ms ease; box-shadow:0 4px 14px ${P.primary}40; }
        .btn-primary:hover { background:${P.primaryHover}; transform:translateY(-1px); box-shadow:0 8px 20px ${P.primary}4D; }
        .btn-ghost { background:transparent; color:#1F1815; border:1.5px solid #E8DDC9; border-radius:999px; padding:14px 26px; font-weight:500; font-size:14px; display:inline-flex; align-items:center; gap:8px; transition:all 200ms ease; }
        .btn-ghost:hover { background:#FDFAF3; border-color:#1F1815; }
        .soft-card { background:#FDFAF3; border:1px solid #E8DDC9; border-radius:20px; }
        .pill { display:inline-flex; align-items:center; gap:6px; padding:6px 12px; border-radius:999px; font-size:11px; letter-spacing:0.05em; font-weight:500; background:${P.tint}; color:${P.tintText}; text-transform:uppercase; }
        .link-underline { position:relative; }
        .link-underline::after { content:''; position:absolute; left:0; right:0; bottom:-2px; height:1px; background:currentColor; opacity:0.3; transition:opacity 200ms; }
        .link-underline:hover::after { opacity:1; }
        .divider-line { height:1px; background:#E8DDC9; }
      `}</style>

      {/* ── Palette switcher (preview-only) ─────────────────────────── */}
      <div className="sticky top-0 z-40 backdrop-blur" style={{ background: "rgba(247,241,230,0.9)", borderBottom: "1px solid #E8DDC9" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center gap-3 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#6B5F52" }}>Accent:</span>
          {paletteKeys.map((k) => {
            const P2 = PALETTES[k];
            const active = k === paletteKey;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setPaletteKey(k)}
                className="flex items-center gap-2 py-1.5 px-3 rounded-full text-xs font-medium transition-all"
                style={{
                  background: active ? "#1F1815" : "transparent",
                  color: active ? "#FDFAF3" : "#1F1815",
                  border: `1px solid ${active ? "#1F1815" : "#E8DDC9"}`,
                }}
              >
                <span className="w-3.5 h-3.5 rounded-full inline-block" style={{ background: P2.primary, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.1)" }}/>
                {P2.name}
              </button>
            );
          })}
          <span className="text-[10px] ml-auto hidden sm:inline" style={{ color: "#6B5F52" }}>Preview only — live landing at <Link to="/" className="underline">/</Link> is unchanged</span>
        </div>
      </div>

      {/* ── Top nav ─────────────────────────────────────────────────── */}
      <header>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: P.primary }}>
              <ShoppingBag size={16} className="text-white" />
            </div>
            <span className="heading text-xl font-semibold">Vendor Vault</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#6B5F52]">
            <a href="#features" className="link-underline hover:text-[#1F1815]">Features</a>
            <a href="#pricing" className="link-underline hover:text-[#1F1815]">Pricing</a>
            <a href="#faq" className="link-underline hover:text-[#1F1815]">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <button type="button" className="text-sm text-[#1F1815] hover:opacity-70 transition-opacity hidden sm:inline">Sign in</button>
            <button type="button" className="btn-primary">Start free <ArrowRight size={14}/></button>
          </div>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 pb-20 lg:pt-16 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <span className="pill"><Sparkles size={12}/> Made for booth days</span>
            <h1 className="heading mt-6 text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.02]">
              The inventory<br/>app that fits<br/>
              <span className="heading-italic" style={{ color: P.primary }}>your booth.</span>
            </h1>
            <p className="mt-7 text-lg text-[#4A4038] leading-relaxed max-w-xl">
              Track your handmade things, print real barcodes, scan-to-sell from your
              phone, and know exactly what to restock before the next show. Built for
              makers, artists, and vendors who actually pack a truck.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <button type="button" className="btn-primary text-base">Start your 14-day Pro trial <ArrowRight size={14}/></button>
              <button type="button" className="btn-ghost text-base">See how it works</button>
            </div>
            <div className="mt-8 flex items-center gap-5 text-xs text-[#6B5F52]">
              <span className="flex items-center gap-1.5"><Check size={13} style={{color:"#6B8E5A"}}/> No credit card</span>
              <span className="flex items-center gap-1.5"><Check size={13} style={{color:"#6B8E5A"}}/> Cancel anytime</span>
              <span className="flex items-center gap-1.5"><Check size={13} style={{color:"#6B8E5A"}}/> Free tier forever</span>
            </div>
          </div>

          {/* ── Hero visual: stacked mock cards ─────────────────────── */}
          <div className="lg:col-span-5 relative">
            <div className="soft-card p-6 relative" style={{ transform: "rotate(-1.5deg)" }}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-[#6B5F52] uppercase tracking-wider">Today · Denver Craft Fair</span>
                <span className="pill" style={{ background: "#E6EDD8", color: "#4A6B3E" }}>Live</span>
              </div>
              <div className="heading text-4xl font-medium">$1,847</div>
              <div className="text-sm text-[#6B5F52] mt-1">42 sales · 6 low on stock</div>
              <div className="divider-line my-5"></div>
              <div className="space-y-3">
                {[
                  { name: "Ceramic mug — sage", sku: "MUG-042", stock: 3, price: 38 },
                  { name: "Enamel pin — moth", sku: "PIN-108", stock: 12, price: 12 },
                  { name: "Screen print — 11×14", sku: "PRT-024", stock: 0, price: 45 },
                ].map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#F0E6D2" }}>
                        <Package size={14} style={{ color: "#6B5F52" }}/>
                      </div>
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-[#6B5F52] font-mono">{p.sku}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">${p.price}</div>
                      <div className="text-xs" style={{ color: p.stock === 0 ? "#B85C50" : p.stock < 5 ? "#B08544" : "#6B5F52" }}>
                        {p.stock === 0 ? "Out" : `${p.stock} left`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Small floating scanner card */}
            <div
              className="soft-card p-4 absolute -bottom-8 -left-4 shadow-xl w-48"
              style={{ transform: "rotate(3deg)", background: "#1F1815", color: "#FDFAF3", borderColor: "#1F1815" }}
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider" style={{ color: "#E8DDC9" }}>
                <ScanLine size={14}/> Scanner
              </div>
              <div className="mt-3 h-16 border-2 border-dashed rounded-lg flex items-center justify-center" style={{ borderColor: "#4A4038" }}>
                <BarcodeIcon size={26} style={{ color: P.primary }}/>
              </div>
              <div className="mt-2 text-[11px]" style={{ color: "#E8DDC9" }}>MUG-042 → Log sale</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature strip ───────────────────────────────────────────── */}
      <section id="features" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="pill">Built for makers</span>
          <h2 className="heading mt-5 text-4xl lg:text-5xl font-medium leading-tight">
            Everything you need,<br/>
            <span className="heading-italic">nothing you don't.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {[
            { icon: BarcodeIcon, title: "Real barcodes", body: "Auto-generated SKUs and Code128 barcodes for every variant. Print on any home printer." },
            { icon: ScanLine, title: "Scan from your phone", body: "Webcam scanner reads your tags instantly. USB / Bluetooth scanners work too." },
            { icon: BarChart3, title: "Know what sold", body: "By-day and sale-by-sale reports per event. Restock exactly what moved." },
            { icon: Wifi, title: "Works offline", body: "Install as a PWA. Sales queue locally when the venue Wi-Fi drops out." },
          ].map((f, i) => (
            <div key={i} className="soft-card p-6">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "#F0E6D2" }}>
                <f.icon size={20} style={{ color: P.primary }}/>
              </div>
              <h3 className="heading text-xl font-medium">{f.title}</h3>
              <p className="mt-2 text-sm text-[#6B5F52] leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────────────────── */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="pill">Pricing</span>
            <h2 className="heading mt-5 text-4xl lg:text-5xl font-medium leading-tight">
              Fair pricing<br/>
              <span className="heading-italic">for real vendors.</span>
            </h2>
            <p className="mt-4 text-[#6B5F52] max-w-md leading-relaxed">
              Start free, upgrade only when you outgrow it. 14-day Pro trial on every new account.
            </p>
          </div>
          <div className="inline-flex p-1 rounded-full border" style={{ borderColor: "#E8DDC9", background: "#FDFAF3" }}>
            <button
              type="button"
              onClick={() => setYearly(false)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${!yearly ? "text-white shadow-sm" : "text-[#6B5F52]"}`}
              style={{ background: !yearly ? "#1F1815" : "transparent" }}
            >Monthly</button>
            <button
              type="button"
              onClick={() => setYearly(true)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all inline-flex items-center gap-2 ${yearly ? "text-white shadow-sm" : "text-[#6B5F52]"}`}
              style={{ background: yearly ? "#1F1815" : "transparent" }}
            >
              Yearly
              <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: yearly ? P.primary : "#F0E6D2", color: yearly ? "white" : "#6B5F52" }}>−20%</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {[
            { name: "Free", icon: Sparkles, monthly: 0, yearly: 0, tagline: "Try Vendor Vault at one show.", features: ["Up to 20 products", "1 active event", "Solo (no team)", "Barcodes, scanner, sales"], cta: "Start free" },
            { name: "Pro", icon: Zap, monthly: 15, yearly: 144, tagline: "For makers running multiple shows.", features: ["Unlimited products & events", "5 team members", "Shopify & Square integrations", "By-day analytics", "Offline write queue"], cta: "Start 14-day trial", highlight: true },
            { name: "Business", icon: Crown, monthly: 39, yearly: 374, tagline: "Full booths and growing teams.", features: ["Everything in Pro", "Unlimited team", "Priority support", "Bulk Shopify sync", "Per-event ledger"], cta: "Start Business" },
          ].map((t, i) => {
            const amount = yearly ? t.yearly : t.monthly;
            const period = t.monthly === 0 ? "" : (yearly ? "/yr" : "/mo");
            const isHi = t.highlight;
            return (
              <div
                key={i}
                className="soft-card p-8 relative"
                style={isHi ? { background: "#1F1815", color: "#FDFAF3", borderColor: "#1F1815", transform: "translateY(-8px)" } : {}}
              >
                {isHi && (
                  <span className="absolute -top-3 left-8 pill" style={{ background: P.primary, color: "white" }}>
                    Most popular
                  </span>
                )}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isHi ? P.primary : "#F0E6D2" }}>
                    <t.icon size={17} style={{ color: isHi ? "white" : P.primary }}/>
                  </div>
                  <div className="heading text-2xl font-medium">{t.name}</div>
                </div>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: isHi ? "#E8DDC9" : "#6B5F52" }}>{t.tagline}</p>
                <div className="mt-7">
                  <div className="heading text-5xl font-medium">
                    ${amount}
                    <span className="text-base font-normal ml-1" style={{ color: isHi ? "#E8DDC9" : "#6B5F52" }}>{period}</span>
                  </div>
                  {yearly && t.monthly > 0 && (
                    <div className="text-xs mt-1" style={{ color: isHi ? "#E8DDC9" : "#6B5F52" }}>≈ ${Math.round(t.yearly/12)}/mo billed yearly</div>
                  )}
                </div>
                <ul className="mt-7 space-y-2.5">
                  {t.features.map((f, k) => (
                    <li key={k} className="flex items-start gap-2 text-sm leading-snug">
                      <Check size={15} className="mt-[2px] flex-shrink-0" style={{ color: isHi ? P.primary : "#6B8E5A" }}/>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="w-full mt-8 py-3 rounded-full font-medium text-sm transition-all"
                  style={isHi ? { background: P.primary, color: "white" } : { background: "transparent", color: "#1F1815", border: "1.5px solid #1F1815" }}
                >
                  {t.cta}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Testimonials ────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="pill">From the makers</span>
          <h2 className="heading mt-5 text-4xl lg:text-5xl font-medium leading-tight">
            Trusted by vendors<br/>
            <span className="heading-italic">at every stall.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {[
            { q: "Setup at the next con took ten minutes instead of an hour. Barcodes at home, scan, sell, done. The booth feels like a real shop now.", name: "Maya O.", role: "Ceramics & pins · Brooklyn" },
            { q: "I run six shows a season and used to lose track of which shirts moved where. The per-event report tells me what to restock before the next gate opens.", name: "Derek L.", role: "Apparel · Tattoo conventions" },
            { q: "Hiring my partner as Staff for the weekend with a single invite is the killer feature. No more 'wait, what's the price on that?'", name: "Aiko T.", role: "Stickers · Artist alleys" },
          ].map((t, i) => (
            <div key={i} className="soft-card p-7 flex flex-col">
              <Quote size={28} style={{ color: P.primary }}/>
              <p className="mt-4 heading text-lg leading-snug flex-1" style={{ letterSpacing: "-0.01em" }}>
                "{t.q}"
              </p>
              <div className="mt-6 pt-5" style={{ borderTop: "1px solid #E8DDC9" }}>
                <div className="font-semibold text-sm">{t.name}</div>
                <div className="text-xs text-[#6B5F52] mt-0.5">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────── */}
      <section id="faq" className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="mb-10">
          <span className="pill">Questions</span>
          <h2 className="heading mt-5 text-4xl lg:text-5xl font-medium leading-tight">FAQ</h2>
          <p className="mt-4 text-[#6B5F52] leading-relaxed">
            Can't find what you need? Email <a href="#" className="underline" style={{color:P.primary}}>support@vendorvaultonline.com</a>.
          </p>
        </div>

        <div className="space-y-3">
          {[
            { q: "Is there really a free plan?", a: "Yes — 20 products and 1 event, forever. Every new account also gets a 14-day Pro trial automatically." },
            { q: "How do barcodes work?", a: "Every variant gets an auto-generated SKU and Code128 barcode. Print them on regular sticker paper or shipping-tag stock with any inkjet/laser printer." },
            { q: "Can I scan with my phone?", a: "Yes. The scanner uses your phone's webcam — no app install. USB / Bluetooth scanners also work on show day." },
            { q: "Does it work offline?", a: "Yes. Install Vendor Vault as a PWA. Reads work offline from a local cache, and sales queue locally to auto-sync when you're back online." },
            { q: "Can I sync with Shopify or take cards with Square?", a: "Both, on Pro and Business. Shopify: one-click OAuth then push your whole catalog. Square: deep-link into the Square POS app with the total pre-filled." },
          ].map((f, i) => {
            const open = i === openFaq;
            return (
              <div key={i} className="soft-card overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  className="w-full px-6 py-5 flex items-start justify-between gap-4 text-left"
                  style={{ background: open ? "#F0E6D2" : "transparent" }}
                >
                  <span className="heading text-lg font-medium">{f.q}</span>
                  <ChevronDown size={20} className="flex-shrink-0 mt-1 transition-transform" style={{ transform: open ? "rotate(180deg)" : "none", color: "#6B5F52" }}/>
                </button>
                {open && (
                  <div className="px-6 pb-6 pt-1 text-sm text-[#4A4038] leading-relaxed">{f.a}</div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="soft-card p-10 lg:p-16 text-center" style={{ background: "#1F1815", color: "#FDFAF3", borderColor: "#1F1815" }}>
          <span className="pill" style={{ background: "#4A4038", color: "#E8DDC9" }}>Ready?</span>
          <h2 className="heading mt-5 text-4xl lg:text-6xl font-medium leading-tight">
            Your next show,<br/>
            <span className="heading-italic" style={{ color: P.primary }}>fully in hand.</span>
          </h2>
          <p className="mt-5 text-lg opacity-80 max-w-xl mx-auto leading-relaxed">
            Free forever. Pro trial included. Install on your phone in under a minute.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button type="button" className="btn-primary text-base">Start free <ArrowRight size={14}/></button>
            <button type="button" className="btn-ghost text-base" style={{ borderColor: "#4A4038", color: "#FDFAF3" }}>Sign in</button>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="divider-line mb-6"></div>
        <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-[#6B5F52]">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: P.primary }}>
              <ShoppingBag size={11} className="text-white" />
            </div>
            <span>Vendor Vault · Built for vendors · © {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center gap-5">
            <a href="#pricing" className="link-underline">Pricing</a>
            <a href="#faq" className="link-underline">FAQ</a>
            <Link to="/terms" className="link-underline">Terms</Link>
            <Link to="/privacy" className="link-underline">Privacy</Link>
            <a href="mailto:support@vendorvaultonline.com" className="link-underline">Support</a>
          </div>
        </div>

        <div className="mt-8 p-4 rounded-xl text-xs text-center" style={{ background: "#FDFAF3", border: "1px solid #E8DDC9", color: "#6B5F52" }}>
          👀 <strong>Design preview only</strong> — this is a mockup of the "warm &amp; artisan" direction. The live landing at <Link to="/" className="underline" style={{ color: P.primary }}>/</Link> is unchanged.
        </div>
      </footer>
    </div>
  );
}
